import React, { useState } from 'react';
import { Edit2, Trash2, Grid3x3, Image as ImageIcon } from 'lucide-react';
import { formatPriceRange } from './menuDataStructure';
import { itemService } from '../../../services/menuitemService';
import { useAuth } from '../../../context/AuthContext';
import { P } from '@/config/permissionCodes';

export default function MenuList({
  items,
  allItems = [],
  viewMode,
  selectedItems,
  onSelectItems,
  onEdit,
  onDelete,
  categories = [],
  selectedCategory = 'all',
  onCategoryChange,
  searchQuery = '',
}) {
  const { hasPermission } = useAuth();

  const canEditItem   = hasPermission(P.BACK_OFFICE.MENU.ITEMS.EDIT);
  const canDeleteItem = hasPermission(P.BACK_OFFICE.MENU.ITEMS.DELETE);

  const handleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      onSelectItems(selectedItems.filter((itemId) => itemId !== id));
    } else {
      onSelectItems([...selectedItems, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      onSelectItems([]);
    } else {
      onSelectItems(items.map((item) => item.id));
    }
  };

  const getCategoryCount = (categoryId) => {
    const itemsToCount = allItems.length > 0 ? allItems : items;
    if (categoryId === 'all') return itemsToCount.length;
    return itemsToCount.filter(
      (item) => String(item.categoryId) === String(categoryId)
    ).length;
  };

  const categoryHasSearchMatch = (categoryId) => {
    if (!searchQuery.trim()) return false;
    const itemsToSearch = allItems.length > 0 ? allItems : items;
    return itemsToSearch.some((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        categoryId === 'all' ||
        String(item.categoryId) === String(categoryId);
      return matchesSearch && matchesCategory;
    });
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-220px)]">
      {/* Category Sidebar */}
      <aside className="w-64 bg-white rounded-lg border border-gray-200 flex-shrink-0 flex flex-col h-full">
        <div className="p-3 bg-slate-50 border-b border-gray-200">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">
            Categories
          </h3>
        </div>

        <nav className="p-2 space-y-1 overflow-y-auto flex-1">
          {/* All Items */}
          <button
            onClick={() => onCategoryChange?.('all')}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              selectedCategory === 'all'
                ? 'bg-teal-50 text-teal-700 shadow-sm'
                : categoryHasSearchMatch('all')
                ? 'bg-yellow-50 text-gray-700 border border-yellow-300'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center gap-2">
              <Grid3x3 className="w-4 h-4" />
              All Items
            </span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                selectedCategory === 'all'
                  ? 'bg-teal-100 text-teal-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {getCategoryCount('all')}
            </span>
          </button>

          {/* Category List */}
          <div className="pt-2 border-t border-gray-100 space-y-1">
            {(categories ?? []).map((category) => {
              const count = getCategoryCount(category.id);
              const isActive = String(selectedCategory) === String(category.id);
              const hasSearchMatch = categoryHasSearchMatch(category.id);

              return (
                <button
                  key={category.id}
                  onClick={() => onCategoryChange?.(category.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all group ${
                    isActive
                      ? 'bg-teal-50 text-teal-700 shadow-sm'
                      : hasSearchMatch
                      ? 'bg-yellow-50 text-gray-700 border border-yellow-300'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="truncate flex-1 text-left pr-2">
                    {category.name}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                      isActive
                        ? 'bg-teal-100 text-teal-700'
                        : hasSearchMatch
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((item) => (
              <MenuGridCard
                key={item.id}
                item={item}
                isSelected={selectedItems.includes(item.id)}
                onSelect={() => handleSelectItem(item.id)}
                onEdit={canEditItem ? () => onEdit(item) : null}
                onDelete={canDeleteItem ? () => onDelete(item.id) : null}
              />
            ))}
          </div>
        ) : (
          <MenuTableView
            items={items}
            selectedItems={selectedItems}
            onSelectItem={handleSelectItem}
            onSelectAll={handleSelectAll}
            onEdit={onEdit}
            onDelete={onDelete}
            canEditItem={canEditItem}
            canDeleteItem={canDeleteItem}
          />
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// TABLE VIEW
// ══════════════════════════════════════════════════════════════════
function MenuTableView({
  items,
  selectedItems,
  onSelectItem,
  onSelectAll,
  onEdit,
  onDelete,
  canEditItem,
  canDeleteItem,
}) {
  const hasAnyAction = canEditItem || canDeleteItem;
  const columnLayout = hasAnyAction
    ? '1.5fr 1fr 1fr 1.5fr 1fr 0.6fr'
    : '1.5fr 1fr 1fr 1.5fr 1fr';

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Table Header */}
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={selectedItems.length === items.length && items.length > 0}
            onChange={onSelectAll}
            className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
          />
          <div
            className="flex-1 grid gap-3 text-xs font-semibold text-gray-700 uppercase"
            style={{ gridTemplateColumns: columnLayout }}
          >
            <div>Item</div>
            <div>Category</div>
            <div>Kitchen Station</div>
            <div>Variants</div>
            <div>Price</div>
            {hasAnyAction && <div className="text-center">Actions</div>}
          </div>
        </div>
      </div>

      {/* Table Body */}
      <div className="divide-y divide-gray-100">
        {items.map((item) => (
          <MenuListRow
            key={item.id}
            item={item}
            isSelected={selectedItems.includes(item.id)}
            onSelect={() => onSelectItem(item.id)}
            onEdit={() => onEdit(item)}
            onDelete={() => onDelete(item.id)}
            columnLayout={columnLayout}
            canEditItem={canEditItem}
            canDeleteItem={canDeleteItem}
          />
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// GRID CARD
// ══════════════════════════════════════════════════════════════════
function MenuGridCard({ item, isSelected, onSelect, onEdit, onDelete }) {
  const prices =
    item?.variants?.map((v) => v.variantPrice ?? item.basePrice ?? 0) || [];
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange =
    prices.length > 1
      ? `₹${minPrice.toFixed(2)} - ₹${maxPrice.toFixed(2)}`
      : `₹${(item.basePrice ?? prices[0] ?? 0).toFixed(2)}`;

  const isInactive = item.isActive === false;

  return (
    <div
      className={`bg-white border-2 rounded-xl overflow-hidden shadow-sm transition-all duration-200 relative ${
        isInactive
          ? 'opacity-60 border-gray-300 bg-gray-50'
          : isSelected
          ? 'border-teal-500 ring-2 ring-teal-100 shadow-lg hover:shadow-xl'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-xl'
      }`}
    >
      {/* Header Section */}
      <div className="px-3 py-2.5 bg-slate-50 border-b border-gray-100 relative">
        {/* Image Thumbnail */}
        <div className="absolute top-2 right-2 w-12 h-12 rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm">
          {item.imageUrl ? (
            <img
              src={itemService.getPhotoUrl(item.imageUrl)}
              alt={item.name}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className={`w-full h-full flex items-center justify-center bg-gray-100 ${
              item.imageUrl ? 'hidden' : 'flex'
            }`}
          >
            <ImageIcon className="w-5 h-5 text-gray-400" />
          </div>
        </div>

        <div className="flex items-center justify-between mb-1.5 pr-14">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onSelect}
              className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-2 focus:ring-teal-500 focus:ring-offset-0 cursor-pointer"
            />
            {/* Veg/Non-veg Badge */}
            <div
              className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                item.isVeg
                  ? 'border-green-600 bg-green-50'
                  : 'border-red-600 bg-red-50'
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  item.isVeg ? 'bg-green-600' : 'bg-red-600'
                }`}
              />
            </div>
            {isInactive && (
              <span className="inline-flex items-center px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold border border-red-300">
                INACTIVE
              </span>
            )}
          </div>

          {item.shortcutKey > 0 && !isInactive && (
            <span className="inline-flex items-center justify-center bg-orange-500 text-white text-[10px] font-bold rounded px-2 py-0.5 min-w-[22px] h-[20px] shadow-sm">
              {item.shortcutKey}
            </span>
          )}
        </div>

        <h3 className="font-semibold text-gray-900 text-sm line-clamp-1 pr-14">
          {item.name}
        </h3>
        {item.description && (
          <p className="text-xs text-gray-500 line-clamp-1 mt-1 pr-14">
            {item.description}
          </p>
        )}
        {item.itemCode && (
  <p className="text-[10px] text-slate-400 mt-1">Code: {item.itemCode}</p>
)}
{item.barcode && (
  <p className="text-[10px] text-slate-400">Barcode: {item.barcode}</p>
)}
      </div>

      {/* Content Section */}
      <div className="p-4 space-y-3">
        {/* Category and Station Tags */}
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-xs font-medium border border-slate-200">
            {item.categoryName || item.category || 'Uncategorized'}
          </span>
          {item.kitchenStationName ? (
            <span className="inline-flex items-center px-2.5 py-1 bg-purple-50 text-purple-700 rounded-md text-xs font-medium border border-purple-200">
              {item.kitchenStationName}
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-1 bg-gray-50 text-gray-400 rounded-md text-xs font-medium border border-gray-200">
              No Station
            </span>
          )}
        </div>

        {/* Variants Section */}
        {item.variants && item.variants.length > 0 && (
          <div className="relative group inline-block">
            <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-md font-medium border border-blue-200 cursor-help text-xs">
              {item.variants.length}{' '}
              {item.variants.length === 1 ? 'Variant' : 'Variants'}
            </span>
            {/* Tooltip */}
            <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-48">
              <div className="bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3">
                <div className="font-semibold mb-2 text-gray-200">
                  Variant Prices
                </div>
                <div className="space-y-1.5">
                  {item.variants.map((variant, index) => (
                    <div
                      key={variant.id || index}
                      className="flex justify-between items-center"
                    >
                      <span className="text-gray-300">
                        {variant.variantName?.trim()
                          ? variant.variantName
                          : `Variant ${index + 1}`}
                      </span>
                      <span className="font-semibold text-white ml-2">
                        ₹{(variant.variantPrice ?? item.basePrice ?? 0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
              </div>
            </div>
          </div>
        )}

        {/* Price Section */}
        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-medium uppercase">
              Price
            </span>
            <span className="text-xl font-bold text-teal-600">{priceRange}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons — hidden entirely when no permissions */}
      {(onEdit || onDelete) && (
        <div className="px-4 pb-4 flex gap-2">
          {onEdit && (
            <button
              onClick={onEdit}
              className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5 border text-xs font-semibold ${
                isInactive
                  ? 'bg-gray-100 text-gray-500 border-gray-300'
                  : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
              }`}
            >
              <Edit2 size={14} />
              Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5 border text-xs font-semibold ${
                isInactive
                  ? 'bg-gray-100 text-gray-500 border-gray-300'
                  : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
              }`}
            >
              <Trash2 size={14} />
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// TABLE ROW
// ══════════════════════════════════════════════════════════════════
function MenuListRow({
  item,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  columnLayout,
  canEditItem,
  canDeleteItem,
}) {
  const prices =
    item?.variants?.map((v) => v.variantPrice ?? item.basePrice ?? 0) || [];
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange =
    prices.length > 1
      ? `₹${minPrice.toFixed(2)} - ₹${maxPrice.toFixed(2)}`
      : `₹${(item.basePrice ?? prices[0] ?? 0).toFixed(2)}`;

  const isInactive = item.isActive === false;

  return (
    <div
      className={`px-4 py-3 transition-colors ${
        isInactive
          ? 'bg-gray-50 opacity-60'
          : isSelected
          ? 'bg-teal-50'
          : 'hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
        />
        <div
          className="flex-1 grid gap-3 items-center text-sm"
          style={{ gridTemplateColumns: columnLayout }}
        >
          {/* Item */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <div
                className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 ${
                  item.isVeg ? 'border-green-600' : 'border-red-600'
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    item.isVeg ? 'bg-green-600' : 'bg-red-600'
                  }`}
                />
              </div>
              {isInactive && (
                <span className="inline-flex items-center px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold border border-red-300">
                  INACTIVE
                </span>
              )}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{item.name}</p>
              {item.description && (
                <p className="text-xs text-gray-500 truncate">
                  {item.description}
                </p>
              )}
            </div>
          </div>

          {/* Category */}
          <div>
            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
              {item.categoryName || item.category || 'Uncategorized'}
            </span>
          </div>

          {/* Kitchen Station */}
          <div>
            {item.kitchenStationName ? (
              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                {item.kitchenStationName}
              </span>
            ) : (
              <span className="inline-block px-2 py-1 bg-gray-50 text-gray-400 rounded text-xs font-medium">
                No Station
              </span>
            )}
          </div>

          {/* Variants */}
          <div className="truncate text-xs">
            {item.variants?.length > 0 ? (
              item.variants.map((variant, idx) => (
                <span key={variant.id || idx} className="text-gray-700">
                  {variant.variantName || `Variant ${idx + 1}`}: ₹
                  {(variant.variantPrice ?? item.basePrice ?? 0).toFixed(2)}
                  {idx < item.variants.length - 1 && ', '}
                </span>
              ))
            ) : (
              <span className="text-gray-400">No variants</span>
            )}
          </div>

          {/* Price */}
          <div className="font-bold text-teal-600">{priceRange}</div>

          {/* Actions — cell hidden entirely when no permissions */}
          {(canEditItem || canDeleteItem) && (
            <div className="flex justify-center gap-1">
              {canEditItem && (
                <button
                  onClick={onEdit}
                  className={`p-1.5 rounded transition-colors ${
                    isInactive ? 'text-gray-400' : 'hover:bg-blue-100 text-blue-600'
                  }`}
                  title="Edit"
                >
                  <Edit2 size={16} />
                </button>
              )}
              {canDeleteItem && (
                <button
                  onClick={onDelete}
                  className={`p-1.5 rounded transition-colors ${
                    isInactive ? 'text-gray-400' : 'hover:bg-red-100 text-red-600'
                  }`}
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}